/**
 * <s-gift-guide>
 * "Who's on your list?" → "What's her vibe?" → eight wrapped gift boxes, one per night of Chanukah.
 *   Persona buttons: commandfor="<this id>" command="--pick-N" (N = 1-based panel number), [data-persona="N"].
 *   [data-panel="N"]  one Eight Nights panel per persona; hidden until picked.
 *   Vibe buttons: command="--style-N-K", [data-style-chip="K"]; [data-style-grid="K"] is that vibe's gift grid.
 *     Choosing a vibe shows its grid wrapped, lights the shamash (panel gets data-lit), reveals [data-after-style]
 *     and hides the [data-before-style] prompt.
 *   Gift boxes: [data-wrap] buttons with command="--open" inside [data-night="n"]. Unwrapping sets data-open on the
 *     night and data-lit on [data-candle="n"]. command="--open-all" unwraps the rest one by one.
 *   [data-all-open] shows once all eight nights in the current grid are open, and the lit menorah scrolls back into view.
 *   Hovering or focusing a night brightens its lit candle ([data-glow]).
 */
class SGiftGuide extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.personas = [...this.querySelectorAll('[data-persona]')];
    this.timers = [];

    this.handleCommand = (event) => {
      const command = event.command || '';
      const pickMatch = /^--pick-(\d+)$/.exec(command);
      if (pickMatch) {
        this.pick(pickMatch[1]);
        return;
      }
      const styleMatch = /^--style-(\d+)-(\d+)$/.exec(command);
      if (styleMatch) {
        this.chooseStyle(styleMatch[1], styleMatch[2]);
        return;
      }
      const source = event.source;
      if (!source) return;
      if (command === '--open') {
        const night = source.closest('[data-night]');
        if (night) this.open(night, true);
      } else if (command === '--open-all') {
        const panel = source.closest('[data-panel]');
        if (panel) this.openAll(panel);
      }
    };
    this.handleOver = (event) => {
      const panel = event.target.closest('[data-panel]');
      if (!panel) return;
      const night = event.target.closest('[data-night]');
      for (const candle of panel.querySelectorAll('[data-candle]')) {
        candle.toggleAttribute('data-glow', Boolean(night) && candle.dataset.candle === night.dataset.night);
      }
    };
    this.handleOut = (event) => {
      const panel = event.target.closest('[data-panel]');
      if (!panel || panel.contains(event.relatedTarget)) return;
      for (const candle of panel.querySelectorAll('[data-glow]')) candle.removeAttribute('data-glow');
    };

    this.addEventListener('command', this.handleCommand);
    this.addEventListener('pointerover', this.handleOver);
    this.addEventListener('focusin', this.handleOver);
    this.addEventListener('pointerout', this.handleOut);
  }

  disconnectedCallback() {
    this.clearTimers();
    this.removeEventListener('command', this.handleCommand);
    this.removeEventListener('pointerover', this.handleOver);
    this.removeEventListener('focusin', this.handleOver);
    this.removeEventListener('pointerout', this.handleOut);
  }

  get reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  clearTimers() {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers = [];
  }

  pick(number) {
    const panel = this.panels.find((item) => item.dataset.panel === number);
    if (!panel) return;
    this.clearTimers();
    for (const persona of this.personas) {
      persona.setAttribute('aria-pressed', persona.dataset.persona === number ? 'true' : 'false');
    }
    for (const item of this.panels) item.hidden = item !== panel;
    panel.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  chooseStyle(panelNumber, styleNumber) {
    const panel = this.panels.find((item) => item.dataset.panel === panelNumber);
    if (!panel) return;
    this.clearTimers();
    for (const chip of panel.querySelectorAll('[data-style-chip]')) {
      chip.setAttribute('aria-pressed', chip.dataset.styleChip === styleNumber ? 'true' : 'false');
    }
    let grid = null;
    for (const item of panel.querySelectorAll('[data-style-grid]')) {
      const isCurrent = item.dataset.styleGrid === styleNumber;
      item.hidden = !isCurrent;
      if (isCurrent) grid = item;
    }
    if (!grid) return;
    // Re-wrap every box so each vibe is a fresh set of presents.
    for (const night of grid.querySelectorAll('[data-night]')) {
      night.removeAttribute('data-open');
      const wrap = night.querySelector('[data-wrap]');
      if (wrap) {
        wrap.removeAttribute('tabindex');
        wrap.removeAttribute('aria-hidden');
      }
    }
    for (const candle of panel.querySelectorAll('[data-candle]')) candle.removeAttribute('data-lit');
    for (const after of panel.querySelectorAll('[data-after-style]')) after.hidden = false;
    for (const before of panel.querySelectorAll('[data-before-style]')) before.hidden = true;
    for (const done of panel.querySelectorAll('[data-all-open]')) done.hidden = true;
    for (const button of panel.querySelectorAll('[data-open-all]')) button.hidden = false;

    panel.removeAttribute('data-lit');
    // Reflow so the boxes deal in again.
    void panel.offsetWidth;
    requestAnimationFrame(() => panel.setAttribute('data-lit', ''));

    const menorah = panel.querySelector('[data-menorah]');
    if (menorah) menorah.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  open(night, moveFocus) {
    if (night.hasAttribute('data-open')) return;
    night.setAttribute('data-open', '');
    const wrap = night.querySelector('[data-wrap]');
    if (wrap) {
      wrap.setAttribute('tabindex', '-1');
      wrap.setAttribute('aria-hidden', 'true');
    }
    const panel = night.closest('[data-panel]');
    if (!panel) return;
    const candle = panel.querySelector(`[data-candle="${night.dataset.night}"]`);
    if (candle) candle.setAttribute('data-lit', '');
    if (moveFocus) {
      const link = night.querySelector('[data-gift-link]');
      if (link) link.focus({ preventScroll: true });
    }
    this.checkAllOpen(panel);
  }

  openAll(panel) {
    const grid = panel.querySelector('[data-style-grid]:not([hidden])');
    if (!grid) return;
    const closed = [...grid.querySelectorAll('[data-night]:not([data-open])')];
    const step = this.reduceMotion ? 0 : 260;
    closed.forEach((night, index) => {
      this.timers.push(setTimeout(() => this.open(night, false), index * step));
    });
  }

  checkAllOpen(panel) {
    const grid = panel.querySelector('[data-style-grid]:not([hidden])');
    if (!grid) return;
    const nights = grid.querySelectorAll('[data-night]');
    const open = grid.querySelectorAll('[data-night][data-open]');
    if (nights.length === 0 || open.length < nights.length) return;
    for (const done of panel.querySelectorAll('[data-all-open]')) done.hidden = false;
    for (const button of panel.querySelectorAll('[data-open-all]')) button.hidden = true;
    // Finale: bring the fully lit menorah and "Chag sameach!" into view if it has scrolled away.
    const menorah = panel.querySelector('[data-menorah]');
    if (!menorah) return;
    const box = menorah.getBoundingClientRect();
    if (box.top < 0 || box.bottom > window.innerHeight) {
      const delay = this.reduceMotion ? 0 : 700;
      this.timers.push(
        setTimeout(() => menorah.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' }), delay)
      );
    }
  }
}

if (!customElements.get('s-gift-guide')) customElements.define('s-gift-guide', SGiftGuide);
